import { Button, Card, CardContent, CardDescription, CardFooter, CardHeaderImage, CardText, Icon, ManageLicense, ModelProperty } from '@/components/wm';
import * as React from 'react';
import { useState } from 'react';

// WiseAPI's interactive reference (Swagger UI), served by the API itself
const WISE_API_REFERENCE_PATH = '/v1/docs/01a0d300-818e-7161-a739-70e06a63930a/';

export function Partner ({ partner, license, organizationId }) {
    const [modalOpen, setModalOpen] = useState(false);

    const
        image = `/images/integration-partners/${partner.slug}.png`,
        slug = partner.slug,
        state = partner.state,
        plan = WiseMetering.plans.get(partner.plan_requirement_id),
        documentation = partner.documentation || {},
        documentationUrl = documentation.customer,
        internalDocumentationUrl = WiseMetering.currentUser.isSuperUser() ? documentation.internal : null,
        apiReferenceUrl = slug === 'wise_api' ? `${WiseMetering.apiUrl}${WISE_API_REFERENCE_PATH}` : null;

    const determineButtonState = () => {
        if (state !== 'active') {
            return { label: i18next.t('integrations.licenses.upcoming'), disabled: true };
        }

        if (!WiseMetering.Permissions.hasIntegrationPermissions(plan.get('slug'))) {
            const navigateToPlans = () => Backbone.history.navigate(`/organizations/${organizationId}`, { trigger: true });

            return {
                label: i18next.t('integrations.licenses.upgrade_plan', { plan: plan.get('name') }),
                disabled: false,
                style: 'tw-opacity-65 hover:tw-opacity-100',
                onClick: () => navigateToPlans()
            };
        }

        if (WiseMetering.Permissions.hasRolePermissions('integrationLicenses', 'create')) {
            if (!license) {
                return { label: i18next.t('integrations.licenses.activate_license'), disabled: false, onClick: () => createLicense() };
            }
            if (license && license.state === 'request') {
                return { label: i18next.t('integrations.licenses.requested'), disabled: false, onClick: () => navigateToLicense() };
            }
            if (license && license.state === 'active') {
                return { label: i18next.t('ui.actions.manage'), disabled: false, onClick: () => navigateToLicense() };
            }
        }

        if (WiseMetering.Permissions.hasRolePermissions('integrationLicenses', 'request')) {
            if (!license) {
                return { label: i18next.t('integrations.licenses.request_activation'), disabled: false, onClick: () => createLicense() };
            }
            if (license && license.state === 'request') {
                return { label: i18next.t('integrations.licenses.activation_requested'), disabled: true };
            }
            if (license && license.state === 'active') {
                return { label: i18next.t('ui.actions.manage'), disabled: false, onClick: () => navigateToLicense() };
            }
        }

        return { label: i18next.t('integrations.licenses.upcoming'), disabled: true };
    };

    const
        buttonState = determineButtonState(),
        createLicense = () => setModalOpen(true),
        navigateToLicense = () => Backbone.history.navigate(`/integration_licenses/${license.id}/${license.total === 0 ? 'licenses' : 'integrations'}`, { trigger: true });

    let modelPropertyTotal = <ModelProperty property="total" type="hidden" value={0}/>;

    if (WiseMetering.currentUser.isSuperUser()) {
        modelPropertyTotal = <ModelProperty dataType="int" label={i18next.t('integrations.licenses.total')} property="total" type="number"/>;
    }

    let description = i18next.t('integrations.licenses.modal_description');

    if (WiseMetering.currentUser.isSuperUser()) {
        description = '';
    }

    return (
        <div>
            {modalOpen && (
                <ManageLicense
                    description={description}
                    mode={license ? 'edit' : 'create'}
                    model={license}
                    onClose={() => setModalOpen(false)}
                    title={i18next.t('integrations.licenses.activate_license')}
                >
                    {modelPropertyTotal}
                    <ModelProperty label={
                        WiseMetering.currentUser.isSuperUser() ?
                            i18next.t('integrations.licenses.activation_reason') :
                            i18next.t('common.comment').capitalize()
                    } property="comment" type="text"/>
                    <ModelProperty property="partner_id" type="hidden" value={partner.id}/>
                    <ModelProperty property="organization_id" type="hidden" value={organizationId}/>
                </ManageLicense>
            )}
            <Card className="tw-h-full">
                <CardHeaderImage>
                    <img alt={partner.name} src={image}/>
                </CardHeaderImage>
                <CardDescription>
                    <span>
                        {partner.name}
                    </span>
                    <span>
                        {partner.homepage && (
                            <a href={partner.homepage} rel="noopener noreferrer" target="_blank">
                                <Button size="socialIcon" variant="ghost">
                                    <Icon icon="globe"/>
                                </Button>
                            </a>
                        )}
                        {partner.linkedin && (
                            <a href={partner.linkedin} rel="noopener noreferrer" target="_blank">
                                <Button size="socialIcon" variant="ghost">
                                    <Icon icon="linkedIn"/>
                                </Button>
                            </a>
                        )}
                        {documentationUrl && (
                            <a href={documentationUrl} rel="noopener noreferrer" target="_blank" title={i18next.t('integration_partners.documentation')}>
                                <Button size="socialIcon" variant="ghost">
                                    <Icon icon="document"/>
                                </Button>
                            </a>
                        )}
                        {internalDocumentationUrl && (
                            <a href={internalDocumentationUrl} rel="noopener noreferrer" target="_blank" title={i18next.t('integration_partners.internal_documentation')}>
                                <Button size="socialIcon" variant="ghost">
                                    <Icon icon="internalDocument"/>
                                </Button>
                            </a>
                        )}
                        {apiReferenceUrl && (
                            <a href={apiReferenceUrl} rel="noopener noreferrer" target="_blank" title={i18next.t('integration_partners.api_reference')}>
                                <Button size="socialIcon" variant="ghost">
                                    <Icon icon="apiReference"/>
                                </Button>
                            </a>
                        )}
                    </span>
                </CardDescription>
                <CardContent>
                    <CardText title={i18next.t('common.description').capitalize()}>
                        {i18next.t(`integration_partners.partners.${slug}.description`)}
                    </CardText>
                    <CardText title={i18next.t('common.benefits').capitalize()}>
                        {i18next.t(`integration_partners.partners.${slug}.benefits`)}
                    </CardText>
                    <CardText title={i18next.t('integration_partners.default_plan').capitalize()}>
                        {partner.includedInPlans}
                    </CardText>
                    <CardText title={i18next.t('integration_partners.plan_requirement').capitalize()}>
                        {partner.planRequirement.capitalize()}
                    </CardText>
                </CardContent>
                <CardFooter>
                    <Button
                        className={`tw-w-full ${buttonState.style ? buttonState.style : ''}`}
                        disabled={buttonState.disabled}
                        onClick={buttonState.onClick}
                        type="button"
                    >
                        {buttonState.label}
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}
